/*
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

/**
 * The controller for the home page.
 */
angular.module('home').controller('homeController', ['$scope', '$injector', '$timeout',
        function homeController($scope, $injector, $timeout) {

    // Get required types
    var ConnectionGroup   = $injector.get('ConnectionGroup');
    var GroupListItem     = $injector.get('GroupListItem');
    var ClientIdentifier  = $injector.get('ClientIdentifier');
            
    // Get required services
    var authenticationService  = $injector.get('authenticationService');
    var cacheService           = $injector.get('cacheService');
    var connectionGroupService = $injector.get('connectionGroupService');
    var dataSourceService      = $injector.get('dataSourceService');
    var preferenceService      = $injector.get('preferenceService');
    var requestService         = $injector.get('requestService');

    /**
     * Decodes a JWT token and returns the payload.
     * 
     * @param {string} token
     *     The JWT token to decode.
     * 
     * @returns {Object}
     *     The decoded payload, or null if decoding fails.
     */
    var decodeJWT = function decodeJWT(token) {
        try {
            var parts = token.split('.');
            if (parts.length !== 3) {
                return null;
            }
            
            // Decode the payload (second part)
            var payload = parts[1];
            
            // Add padding if needed
            payload += ([
                '',
                '===',
                '==',
                '='
            ])[payload.length % 4];
            
            // Translate base64url to base64
            var base64 = payload.replace(/[-_]/g, function(char) {
                return {
                    '-' : '+',
                    '_' : '/'
                }[char];
            });
            
            // Decode and parse JSON
            var decoded = atob(base64);
            return JSON.parse(decoded);
        } catch (e) {
            console.warn('Failed to decode JWT token:', e);
            return null;
        }
    };

    /**
     * Extracts groups from the current user's JWT token.
     * Falls back to hardcoded groups if token parsing fails.
     * 
     * @returns {Array}
     *     Array of group IDs.
     */
    var getGroupsFromToken = function getGroupsFromToken() {
        try {
            var token = authenticationService.getCurrentToken();
            if (!token) {
                console.warn('No authentication token available, using fallback groups');
                return getFallbackGroups();
            }
            
            var decoded = decodeJWT(token);
            if (!decoded) {
                console.warn('Failed to decode authentication token, using fallback groups');
                return getFallbackGroups();
            }
            
            // Extract groups from the token (try common claim names)
            var groups = decoded.groups || decoded.memberOf || decoded.role || [];
            
            // Ensure groups is an array
            if (!Array.isArray(groups)) {
                groups = [groups];
            }
            
            // Filter out empty/null values
            groups = groups.filter(function(group) {
                return group && typeof group === 'string' && group.trim().length > 0;
            });
            
            if (groups.length === 0) {
                console.warn('No groups found in token, using fallback groups');
                return getFallbackGroups();
            }
            
            console.log('Extracted groups from token:', groups);
            return groups;
        } catch (e) {
            console.error('Error extracting groups from token, using fallback groups:', e);
            return getFallbackGroups();
        }
    };

    /**
     * Returns fallback group IDs when token parsing fails.
     * This ensures the frontend continues to work.
     * 
     * @returns {Array}
     *     Array of fallback group IDs.
     */
    var getFallbackGroups = function getFallbackGroups() {
        // Use the same hardcoded groups that were working before
        return ['fc63fa08-351c-4b9f-a976-c92416b07ae9', 'e5d611db-8d24-4193-bc3f-8dafaf4a7403'];
    };

    /**
     * Map of data source identifier to the root connection group of that data
     * source, or null if the connection group hierarchy has not yet been
     * loaded.
     *
     * @type Object.<String, ConnectionGroup>
     */
    $scope.rootConnectionGroups = null;

    /**
     * Array of all connection properties that are filterable.
     *
     * @type String[]
     */
    $scope.filteredConnectionProperties = [
        'name'
    ];

    /**
     * Array of all connection group properties that are filterable.
     *
     * @type String[]
     */
    $scope.filteredConnectionGroupProperties = [
        'name'
    ];

    /**
     * Available labs for selection.
     *
     * @type Array
     */
    $scope.availableLabs = [];

    /**
     * Lab selection state object (using object to avoid scope inheritance issues).
     *
     * @type Object
     */
    $scope.labSelection = {
        selectedLab: ''
    };

    // Debug watcher for selectedLab
    $scope.$watch('labSelection.selectedLab', function(newVal, oldVal) {
        console.log('*** labSelection.selectedLab changed: old=' + oldVal + ', new=' + newVal + ' ***');
    });

    /**
     * Array of local actions available in the user menu.
     *
     * @type Object[]
     */
    $scope.localActions = [];

    // Add admin menu items if user is admin
    var adminPermissionService = $injector.get('adminPermissionService');
    if (adminPermissionService.isAdmin()) {
        $scope.localActions.push({
            name: 'Admin Dashboard',
            className: 'admin-dashboard-menu-item',
            callback: function() {
                window.location.hash = '/nsadmin/dashboard';
            }
        });
        $scope.localActions.push({
            name: 'Lab Management',
            className: 'admin-labs-menu-item',
            callback: function() {
                window.location.hash = '/nsadmin/labs';
            }
        });
        $scope.localActions.push({
            name: 'Pod Management',
            className: 'admin-pods-menu-item',
            callback: function() {
                window.location.hash = '/nsadmin/pods';
            }
        });
        $scope.localActions.push({
            name: 'User Management',
            className: 'admin-users-menu-item',
            callback: function() {
                window.location.hash = '/nsadmin/users';
            }
        });
    }

    // Add lab selection action
    $scope.localActions.push({
        name: 'Select Lab',
        className: 'select-lab-menu-item',
        callback: function() {
            $scope.selectLab();
        }
    });

    /**
     * Loads available labs for the current user.
     */
    /**
     * Loads available labs for the current user.
     */
    $scope.loadAvailableLabs = function loadAvailableLabs() {
        console.log('=== loadAvailableLabs called ===');
        
        // Get user email from authentication service
        var userEmail = authenticationService.getCurrentUsername();
        console.log('Current user email:', userEmail);
        
        // Fallback to hardcoded email if authentication fails
        if (!userEmail) {
            userEmail = 'Deven@northstar-tek.com';
            console.log('Using fallback user email:', userEmail);
        }

        // Get groups from token with fallback
        var groupIds = getGroupsFromToken();
        console.log('Using group IDs:', groupIds);

        // Call the API to get available labs
        console.log('Calling available-labs API...');
        var apiBaseUrl = window.location.origin;
        fetch(apiBaseUrl + '/api/available-labs', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            credentials: 'include',
            body: JSON.stringify({
                user_email: userEmail,
                group_ids: groupIds
            })
        })
        .then(function(response) {
            console.log('API response status:', response.status);
            return response.json();
        })
        .then(function(data) {
            console.log('Available labs API response:', data);
            if (data.labs) {
                console.log('Setting availableLabs:', data.labs);
                $scope.availableLabs = data.labs;
                
                // Load previously selected lab from localStorage
                var savedLab = localStorage.getItem('selectedLab');
                if (savedLab && $scope.availableLabs.some(function(lab) { return lab.id === savedLab; })) {
                    $scope.labSelection.selectedLab = savedLab;
                    console.log('Restored selected lab from localStorage:', savedLab);
                }
                
                console.log('Triggering $apply...');
                $scope.$apply(); // Trigger digest cycle
                console.log('$apply complete, availableLabs length:', $scope.availableLabs.length);
            } else if (data.error) {
                console.error('Error loading labs:', data.error);
            }
        })
        .catch(function(error) {
            console.error('Error fetching available labs:', error);
        });
    };

    /**
     * Flag to indicate lab selection is in progress.
     */
    $scope.isSelectingLab = false;

    /**
     * Refreshes the connection list from the server.
     * 
     * @param {Function} callback
     *     Optional callback to execute after refresh completes.
     */
    $scope.refreshConnections = function refreshConnections(callback) {
        console.log('=== refreshConnections called ===');
        
        // Clear all caches to force fresh data from server
        console.log('=== Clearing all caches ===');
        cacheService.connections.removeAll();
        cacheService.users.removeAll();
        
        // Reset the connection groups to force UI refresh
        $scope.rootConnectionGroups = null;
        $scope.filteredRootConnectionGroups = null;
        
        dataSourceService.apply(
            connectionGroupService.getConnectionGroupTree,
            authenticationService.getAvailableDataSources(),
            ConnectionGroup.ROOT_IDENTIFIER
        )
        .then(function rootGroupsRetrieved(rootConnectionGroups) {
            console.log('=== Connections data received ===');
            
            // Log the structure to debug
            var totalConnections = 0;
            angular.forEach(rootConnectionGroups, function(group, dataSource) {
                console.log('Data source:', dataSource);
                if (group.childConnections) {
                    console.log('  Direct connections:', group.childConnections.length);
                    totalConnections += group.childConnections.length;
                }
                if (group.childConnectionGroups) {
                    console.log('  Connection groups:', group.childConnectionGroups.length);
                    angular.forEach(group.childConnectionGroups, function(cg) {
                        var cgConns = cg.childConnections ? cg.childConnections.length : 0;
                        console.log('    Group:', cg.name, 'connections:', cgConns);
                        totalConnections += cgConns;
                    });
                }
            });
            console.log('=== Total connections found:', totalConnections, '===');
            
            $scope.rootConnectionGroups = rootConnectionGroups;
            console.log('=== Scope updated, triggering digest ===');
            
            // Force digest cycle if not already in one
            if (!$scope.$$phase && !$scope.$root.$$phase) {
                $scope.$apply();
            }
            
            if (callback) {
                console.log('=== Calling refresh callback ===');
                callback();
            }
        }, requestService.DIE);
    };

    /**
     * Selects the chosen lab and updates connections.
     */
    $scope.selectLab = function selectLab() {
        console.log('=== selectLab called ===');
        console.log('Selected lab value:', $scope.labSelection.selectedLab);
        console.log('Type of selectedLab:', typeof $scope.labSelection.selectedLab);
        console.log('availableLabs:', $scope.availableLabs);
        
        // Check if selectedLab is falsy (empty string, null, undefined)
        if (!$scope.labSelection.selectedLab) {
            console.log('No lab selected (falsy), returning');
            return;
        }
        
        // Also check for the string "undefined" or empty
        if ($scope.labSelection.selectedLab === '' || $scope.labSelection.selectedLab === 'undefined') {
            console.log('No lab selected (empty/undefined string), returning');
            return;
        }

        // Show loading state
        console.log('*** PROCEEDING WITH LAB SELECTION:', $scope.labSelection.selectedLab, '***');
        $scope.isSelectingLab = true;
        // Don't clear rootConnectionGroups here - let refreshConnections update it
        // Setting to null causes errors in guacGroupList countActiveConnections

        // Save to localStorage
        localStorage.setItem('selectedLab', $scope.labSelection.selectedLab);

        // Get user email from authentication
        var userEmail = authenticationService.getCurrentUsername();
        console.log('Current user email:', userEmail);
        
        // Fallback to hardcoded email if authentication fails
        if (!userEmail) {
            userEmail = 'Deven@northstar-tek.com';
            console.log('Using fallback user email:', userEmail);
        }

        // Get groups from token with fallback
        var groupIds = getGroupsFromToken();
        console.log('Using group IDs for lab selection:', groupIds);

        // Call the API
        console.log('Making API call to select-lab...');
        var apiBaseUrl = window.location.origin;
        fetch(apiBaseUrl + '/api/select-lab', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            credentials: 'include',
            body: JSON.stringify({
                user_email: userEmail,
                lab_id: $scope.labSelection.selectedLab,
                group_ids: groupIds
            })
        })
        .then(function(response) {
            console.log('API response received:', response);
            return response.json();
        })
        .then(function(data) {
            console.log('API response data:', data);
            if (data.error) {
                $scope.isSelectingLab = false;
                $scope.$apply();
                alert('Error: ' + data.error);
            } else {
                console.log('=== CLIENT: SELECT_LAB COMPLETED ===');
                var connCount = data.final_permission_count || 0;
                console.log('Lab selected! ' + connCount + ' connections available.');
                
                // Use $timeout instead of setTimeout for Angular digest cycle
                $timeout(function() {
                    console.log('Timeout fired, refreshing connections...');
                    // Refresh the connection list
                    $scope.refreshConnections(function() {
                        console.log('Refresh complete, clearing loading state');
                        $scope.isSelectingLab = false;
                    });
                }, 500);
            }
        })
        .catch(function(error) {
            console.error('Error selecting lab:', error);
            $scope.isSelectingLab = false;
            $scope.$apply();
            alert('Error selecting lab');
        });
    };

    /**
     * Returns whether critical data has completed being loaded.
     *
     * @returns {Boolean}
     *     true if enough data has been loaded for the user interface to be
     *     useful, false otherwise.
     */
    $scope.isLoaded = function isLoaded() {

        return $scope.rootConnectionGroups !== null;

    };

    // Retrieve root groups and all descendants
    dataSourceService.apply(
        connectionGroupService.getConnectionGroupTree,
        authenticationService.getAvailableDataSources(),
        ConnectionGroup.ROOT_IDENTIFIER
    )
    .then(function rootGroupsRetrieved(rootConnectionGroups) {
        $scope.rootConnectionGroups = rootConnectionGroups;
        
        // Load available labs for the user
        $scope.loadAvailableLabs();
    }, requestService.DIE);

    /**
     * Recursively collects all connection identifiers from connection groups.
     * 
     * @param {Object|Array} groups
     *     Object with data source keys and connection group values, or array of connection groups.
     * 
     * @returns {Array}
     *     Array of connection identifiers.
     */
    var collectAllConnectionIdentifiers = function collectAllConnectionIdentifiers(groups, dataSource) {
        var identifiers = [];
        
        if (!groups) {
            console.log('collectAllConnectionIdentifiers: No groups provided');
            return identifiers;
        }
        
        // Handle object format (data source -> connectionGroup)
        if (!Array.isArray(groups)) {
            console.log('collectAllConnectionIdentifiers: Processing object with data sources:', Object.keys(groups));
            // Process each data source separately
            for (var ds in groups) {
                if (groups.hasOwnProperty(ds)) {
                    identifiers = identifiers.concat(collectAllConnectionIdentifiers([groups[ds]], ds));
                }
            }
            return identifiers;
        }
        
        // Process each group in the array
        groups.forEach(function(group) {
            console.log('collectAllConnectionIdentifiers: Processing group:', group.name || 'ROOT', 
                        'childConnections:', group.childConnections ? group.childConnections.length : 0,
                        'childGroups:', group.childConnectionGroups ? group.childConnectionGroups.length : 0);
            
            // Get the data source from the group if not provided
            var currentDataSource = dataSource || group.dataSource || authenticationService.getDataSource();
            
            // Add connections from this group
            if (group.childConnections) {
                group.childConnections.forEach(function(connection) {
                    console.log('collectAllConnectionIdentifiers: Adding connection:', connection.name, 'id:', connection.identifier);
                    // Generate a proper client identifier using ClientIdentifier.toString()
                    var identifier = ClientIdentifier.toString({
                        dataSource : currentDataSource,
                        type       : ClientIdentifier.Types.CONNECTION,
                        id         : connection.identifier
                    });
                    if (identifier) {
                        identifiers.push(identifier);
                    }
                });
            }
            
            // Recursively process child groups
            if (group.childConnectionGroups) {
                identifiers = identifiers.concat(collectAllConnectionIdentifiers(group.childConnectionGroups, currentDataSource));
            }
        });
        
        return identifiers;
    };

    /**
     * Creates a tiled client URL for ALL available connections.
     * 
     * @returns {string}
     *     The URL for viewing all connections in tiled mode, or null if no connections available.
     */
    $scope.getAllConnectionsTiledURL = function getAllConnectionsTiledURL() {
        
        if (!$scope.rootConnectionGroups) {
            return null;
        }
        
        var identifiers = collectAllConnectionIdentifiers($scope.rootConnectionGroups);
        
        // Debug logging - can be removed later
        console.log('getAllConnectionsTiledURL: Found', identifiers.length, 'connections');
        console.log('getAllConnectionsTiledURL: Identifiers:', identifiers);
        
        if (identifiers.length === 0) {
            return null;
        }
        
        var url = '#/client/' + identifiers.join('.');
        console.log('getAllConnectionsTiledURL: Generated URL length:', url.length);
        return url;
    };

}]);

